import { AppDispatch } from "../../dataManagement/uiDb/UiDb";
import { updateBusiness } from "../thunks/BusinessThunk";
import { UpdateBusinessPayload } from "../../interfaces/BusinessObject";
import { MediaRecord } from "../../interfaces/MediaObject";

type BusinessMediaField = "logoUrl" | "bannerUrl";

/**
 * Builds a `persist` callback for useMediaUpload that sets a single URL field
 * (logoUrl or bannerUrl) on the business via the updateBusiness thunk —
 * dispatch, then check `.fulfilled`, then throw a field-specific fallback
 * message on rejection. Shared by useBusinessLogoUpload,
 * useBusinessBannerUpload, and CreateBusinessModal's deferred (create-then-
 * upload) logo flow, which previously each hand-rolled this same shape.
 *
 * `getBusinessId` is a getter rather than a plain value so callers that only
 * know the businessId after an async step (e.g. CreateBusinessModal, where
 * the business doesn't exist until create() resolves) can read it from a ref
 * at call time instead of needing it upfront.
 */
export function makeBusinessFieldPersist(
  dispatch: AppDispatch,
  field: BusinessMediaField,
  getBusinessId: () => string | undefined,
  fallbackMessage: string
) {
  return async (records: MediaRecord[]): Promise<MediaRecord[]> => {
    const record = records[0];
    const businessId = getBusinessId();
    if (!businessId || !record) return records;

    const payload: UpdateBusinessPayload =
      field === "logoUrl"
        ? { businessId, logoUrl: record.publicUrl }
        : { businessId, bannerUrl: record.publicUrl };

    const action = await dispatch(updateBusiness(payload));
    if (!updateBusiness.fulfilled.match(action)) {
      throw new Error((action.payload as string) || fallbackMessage);
    }
    return records;
  };
}
